<template>
  <b-modal
    id="fb-share-modal"
    ref="modal"
    title="Share..."
    size="lg"
    :hide-footer="!shouldShowModalFooter"
    @hide="onclose"
    @show="onShow"
  >
    <div
      v-if="isLoading"
      class="justify-content-center align-items-center loading custom-styles"
    >
      <div
        class="spinner-border text-primary"
        role="status"
      >
        <span class="sr-only">Loading...</span>
      </div>
    </div>

    <b-form-group label="Mode">
      <b-form-radio-group
        v-model="mode"
        :options="options"
        @change="fetchShareLink"
      />
    </b-form-group>

    <b-form-group>
      <b-form-checkbox
        v-model="isLoginRequired"
        :value="true"
        :unchecked-value="false"
        @change="clickLoginRequired"
      >
        Login required
      </b-form-checkbox>
    </b-form-group>

    <b-form-group v-show="isOneSelected">
      <div class="d-flex align-items-center">
        <b-form-checkbox
          v-model="isDirectLinkToFile"
          :value="true"
          :unchecked-value="false"
          @change="clickDirectLinkToFile"
        >
          Direct link to file
        </b-form-checkbox>

        <b-form-checkbox
          v-if="showLiveViewerButton"
          v-model="isLiveViewer"
          :value="true"
          :unchecked-value="false"
          class="ml-3"
          @change="applyLiveViewerPrefix"
        >
          Live Viewer
        </b-form-checkbox>
      </div>

      <b-form-radio-group
        v-if="isDirectLinkToFile && isDirectFileSelection"
        v-model="directLinkType"
        class="mt-2 ml-4"
        stacked
        @change="clickDirectLinkType"
      >
        <b-form-radio value="path">
          Use file path
        </b-form-radio>
        <b-form-radio value="original">
          Use original name
        </b-form-radio>
      </b-form-radio-group>
    </b-form-group>

    <b-form-group>
      <b-form-checkbox
        v-model="isShareToEmail"
        :value="true"
        :unchecked-value="false"
      >
        Send share to email
      </b-form-checkbox>
    </b-form-group>

    <div v-show="isShareToEmail">
      <b-form-group>
        <label for="email-to-share">Please enter emails to share with:</label>
        <b-form-tags
          id="email-to-share"
          v-model="emailToShare"
          placeholder="Email addresses"
        />
      </b-form-group>

      <b-form-group>
        <label for="subject">Subject</label>
        <b-form-input
          id="subject"
          v-model="subject"
        />
      </b-form-group>

      <b-form-group>
        <label for="body">Body</label>
        <b-form-textarea
          id="body"
          v-model="body"
          rows="4"
        />
      </b-form-group>
    </div>

    <b-form-group>
      <b-form-checkbox
        v-model="isNotifyMe"
        :value="true"
        :unchecked-value="false"
      >
        Notify me of activity
      </b-form-checkbox>
    </b-form-group>

    <b-form-group>
      <b-form-checkbox
        v-model="isNotifyOthers"
        :value="true"
        :unchecked-value="false"
      >
        Notify others...
      </b-form-checkbox>
    </b-form-group>

    <b-form-group>
      <b-form-tags
        v-if="isNotifyOthers"
        v-model="notifyOtherEmail"
        placeholder="Email addresses"
      />
    </b-form-group>

    <b-form-group>
      <label>Share a link</label>
      <b-input-group>
        <b-form-input
          ref="share"
          v-model="shareLink"
        />
        <b-input-group-append>
          <b-button
            id="copy-to-clipboard"
            size="sm"
            title="Click to copy"
            variant="secondary"
            @click="copyToClipboard"
          >
            <i
              class="fa fa-clipboard"
              aria-hidden="true"
            />
          </b-button>

          <b-tooltip
            ref="tooltip"
            target="copy-to-clipboard"
            triggers="none"
          >
            Copied
          </b-tooltip>
        </b-input-group-append>
      </b-input-group>
    </b-form-group>

    <template #modal-footer>
      <div class="mt-2 d-flex justify-content-end align-items-center">
        <ButtonOutline
          class="mr-3"
          @click="$refs.modal.hide()"
        >
          Close
        </ButtonOutline>
        <ButtonPrimary @click="sendShareEmail">
          Share
        </ButtonPrimary>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import mixin from "./mixin";
import ButtonOutline from "../../../common/atoms/ButtonOutline.vue";
import ButtonPrimary from "../../../common/atoms/ButtonPrimary.vue";
import FileBrowser from "../../../../services/api/file-browser";

const { mapGetters } = createNamespacedHelpers('fileBrowser')

const getBodyText = link => `Share link: ${link}`
const LIVE_VIEWER_PREFIX = 'https://view.officeapps.live.com/op/view.aspx?src=';

export default {
  name: "ShareModal",
  components: {ButtonPrimary, ButtonOutline},
  mixins: [mixin],
  data: () => ({
    options: [
      { text: 'View', value: 'view' },
      { text: 'Edit', value: 'edit' },
    ],
    isLoading: false,
    mode: 'view',
    isLoginRequired: false,
    isDirectLinkToFile: false,
    directLinkType: 'path',
    isShareToEmail: false,
    isLiveViewer: false,
    emailToShare: [],
    subject: '',
    body: '',
    isNotifyMe: false,
    isNotifyOthers: false,
    notifyOtherEmail: [],
    shareLink: ''
  }),
  computed: {
    ...mapGetters(['selected', 'firstSelectedItem', 'isOneSelected', 'breadcrumbs']),

    shouldShowModalFooter() {
      return this.isShareToEmail || this.isNotifyOthers || this.isNotifyMe;
    },
    showLiveViewerButton() {
      if (!this.shareLink) return false;

      const name = this.shareLink || '';
      const isPPTX = name.toLowerCase().endsWith('.pptx');

      return this.isDirectLinkToFile && isPPTX;
    },
    isDirectFileSelection() {
      return this.isOneSelected && this.firstSelectedItem?.type !== 'folder';
    }
  },
  methods: {
    onShow() {
      this.fetchShareLink();
    },
    copyToClipboard() {
      this.$refs.share.focus()
      this.$refs.share.setSelectionRange(0, this.shareLink.length)
      const success = document.execCommand('copy')
      if (success) {
        this.$refs.tooltip.$emit('open')
      }
    },
    async clickDirectLinkToFile() {
      await this.fetchShareLink();
      if (!this.isDirectLinkToFile) {
        this.isLiveViewer = false;
      }
      this.applyLiveViewerPrefix();
    },
    async clickDirectLinkType() {
      await this.fetchShareLink();
      this.applyLiveViewerPrefix();
    },
    async clickLoginRequired() {
      await this.fetchShareLink();
      this.applyLiveViewerPrefix();
    },
    async fetchShareLink() {
      try {
        const response = await FileBrowser.createShareLink(this.createRequestData());
        this.handleResponse(response);
      } catch (error) {
        console.error(error);
        toastr.error('Failed creating shared link');
      }
    },
    createRequestData() {
      return {
        fileIds: this.selected,
        mode: this.mode,
        type: this.firstSelectedItem.type,
        loginRequired: this.isLoginRequired,
        directLinkToFile: this.generateDirectLinkToFile(),
        directLinkType: this.isDirectLinkToFile ? this.directLinkType : null,
      };
    },
    handleResponse(response) {
      const { data: { link } } = response;
      this.shareLink = link;
      this.body = getBodyText(link);
    },
    async sendShareEmail() {
      try {
        if (this.emailToShare.length) {
          await FileBrowser.sendMail(
            this.subject,
            this.body,
            this.emailToShare.join(','),
            this.shareLink
          )
        }
        
        if (this.notifyOtherEmail.length || this.isNotifyMe) {
          await FileBrowser.updateShareLink({
            isNotifyMe: this.isNotifyMe,
            emails: this.notifyOtherEmail.join(',').replace(/\s+/g, ''),
            sharedUrl: this.shareLink,
          });
        }
      } catch (error) {
        toastr.error('Failed sending email. Try again later.')
      } finally {
        this.$refs.modal.hide()
      }
    },
    generateDirectLinkToFile() {
      if (this.isDirectLinkToFile) {
        return this.firstSelectedItem.type !== 'folder' ? this.firstSelectedItem.path : null;
      }
      
      return null;
    },
    getExtension() {
      if (this.firstSelectedItem.url) {
        const filePath = new URL(this.firstSelectedItem.url).searchParams.get('file');
        const parts = filePath.split('.');

        return parts.length > 1 ? '.' + parts.pop() : '';
      }
      
      return '';
    },
    onclose() {
      this.isDirectLinkToFile = false;
      this.directLinkType = 'path';
      this.isLiveViewer = false;
      this.$refs.modal.hide();
    },
    applyLiveViewerPrefix() {
      if (!this.shareLink) return;
      
      if (this.isLiveViewer) {
        this.shareLink = LIVE_VIEWER_PREFIX + this.shareLink;
      } else {
        this.shareLink = this.shareLink.replace(LIVE_VIEWER_PREFIX, '');
      }
    }
  }
}
</script>

<style scoped lang="scss">
  .custom-styles {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    background: #ffffffb0;
    z-index: 999;
    display: flex;
  }
</style>
